import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'

interface FAQItem {
  question: string
  answer: string
}

const faqs: FAQItem[] = [
  {
    question: 'How does the 7-day free trial work?',
    answer: 'Start using BLP Assistant immediately with full access to all features. You\'ll need to enter a payment method to begin your trial, but you won\'t be charged until the trial period ends. Cancel anytime during the trial and you won\'t be charged.',
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: 'Yes! You can cancel your subscription at any time from your account settings. You will retain access to BLP Assistant until the end of your current billing period.',
  },
  {
    question: 'What happens if I cancel during the trial?',
    answer: 'If you cancel during your 7-day free trial, you will not be charged anything. Your access will continue until the trial period ends, giving you the full trial experience.',
  },
  {
    question: 'Is my payment information secure?',
    answer: 'Absolutely. All payment processing is handled by Stripe, an industry-leading payment processor trusted by millions of businesses worldwide. We never store your credit card information on our servers.',
  },
  {
    question: 'Can I switch between monthly and annual plans?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time from the customer portal. When switching from monthly to annual, you will receive a prorated credit. When switching from annual to monthly, the change takes effect at the end of your current billing period.',
  },
  {
    question: 'What is included in the subscription?',
    answer: 'Your subscription includes unlimited access to the BLP Assistant AI, instant answers during sales calls, compliance-ready responses, objection handling techniques, and all future updates and improvements to the platform.',
  },
  {
    question: 'How do I access my billing information?',
    answer: 'You can access your billing information, update payment methods, view invoices, and manage your subscription through the secure customer portal available in your account settings.',
  },
]

interface FAQItemProps {
  faq: FAQItem
  isOpen: boolean
  onToggle: () => void
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ faq, isOpen, onToggle }) => {
  return (
    <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-xl overflow-hidden transition-all">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-dark-tertiary/30 transition-colors"
      >
        <span className="text-lg font-semibold text-light-primary pr-8">{faq.question}</span>
        <ChevronDown
          className={`w-5 h-5 text-primary-400 flex-shrink-0 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-2">
          <p className="text-light-secondary leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  )
}

export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-light-primary mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-xl text-light-secondary">
          Everything you need to know about BLP Assistant subscription
        </p>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <FAQItemComponent
            key={index}
            faq={faq}
            isOpen={openIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-light-secondary mb-4">Still have questions?</p>
        <a
          href="mailto:support@listeningglass.com"
          className="text-primary-400 hover:text-primary-300 font-semibold transition-colors"
        >
          Contact our support team →
        </a>
      </div>
    </div>
  )
}
